import type { ObjectKind } from "@engine/types";
import { aliensIcons } from "./aliens/icons";
import { animalsIcons } from "./animals/icons";
import { classicIcons } from "./classic/icons";
import { insectsIcons } from "./insects/icons";
import { zombiesIcons } from "./zombies/icons";
import type { ThemeDefinition } from "./types";

/**
 * Every theme the app can dress a case in.
 *
 * A theme changes what a case looks like, what its furniture is called, and who its people are —
 * never the puzzle. The board colours stay light enough that letters and marks remain the loudest
 * things on it, whatever the mood.
 */

const CLASSIC: ThemeDefinition = {
  id: "classic",
  icons: classicIcons,
  colours: {
    rooms: ["#e8ded0", "#d8e3dc", "#e6dce6", "#dee3ef", "#eee2d4", "#dae5e8", "#e9e0e9", "#e2e8da", "#efe3dd", "#dcdfe9"],
    suspectToken: "#1b1a17",
    victimToken: "#8c1c13",
    wall: "#2b2823",
    markMaybe: "#3f3b35",
    markNo: "#9a5b52",
    roomLabel: "#5b564d",
    plate: "#fdfbf7",
    icon: { stroke: "#7d7466", strokeBlocking: "#544d43", fill: "#bdb3a4", accent: "#b9c9d2" },
  },
  objectNames: {
    en: { bed: "bed", chair: "chair", carpet: "carpet", bench: "bench", table: "table", cabinet: "cabinet", plant: "plant", tree: "tree", statue: "statue", crate: "crate", pond: "pond", stove: "stove" },
    nl: { bed: "bed", chair: "stoel", carpet: "kleed", bench: "bank", table: "tafel", cabinet: "kast", plant: "plant", tree: "boom", statue: "standbeeld", crate: "krat", pond: "vijver", stove: "fornuis" },
  },
  premiseKey: "classic",
  namePools: {
    A: { m: "Anton", f: "Amira" }, B: { m: "Bram", f: "Beatrix" }, C: { m: "Casper", f: "Colette" },
    D: { m: "Diego", f: "Delphine" }, E: { m: "Emile", f: "Esther" }, F: { m: "Ferdinand", f: "Fenna" },
    G: { m: "Gustav", f: "Greta" }, H: { m: "Hugo", f: "Helena" }, I: { m: "Ivan", f: "Ingrid" },
    J: { m: "Joris", f: "Juliet" }, K: { m: "Konrad", f: "Katinka" }, L: { m: "Lucas", f: "Lorena" },
    M: { m: "Milan", f: "Margot" }, N: { m: "Nikolas", f: "Nadia" }, O: { m: "Oscar", f: "Odette" },
    P: { m: "Pieter", f: "Paloma" }, V: { m: "Victor", f: "Vivienne" },
  },
};

const ZOMBIES: ThemeDefinition = {
  id: "zombies",
  icons: zombiesIcons,
  colours: {
    rooms: ["#ddd6cb", "#cfd6cd", "#d8cfd8", "#c9d1d6", "#d9d2c4", "#ccd8d2", "#d5ccd0", "#d0d6c8", "#dcd0c8", "#cdd0d8"],
    suspectToken: "#2e3a2c",
    victimToken: "#6e1b10",
    wall: "#211d24",
    markMaybe: "#3a4038",
    markNo: "#8a4a3e",
    roomLabel: "#544f58",
    plate: "#f4f1ea",
    icon: { stroke: "#6d7a66", strokeBlocking: "#403a46", fill: "#a3a294", accent: "#7d9b6a" },
  },
  objectNames: {
    en: { bed: "coffin", chair: "rocking chair", carpet: "magic circle", bench: "tomb slab", table: "table", cabinet: "locked chest", plant: "snapping plant", tree: "dead tree", statue: "gargoyle", crate: "crate", pond: "pool of slime", stove: "cauldron" },
    nl: { bed: "doodskist", chair: "schommelstoel", carpet: "magische cirkel", bench: "grafplaat", table: "tafel", cabinet: "gesloten kist", plant: "bijtplant", tree: "dode boom", statue: "waterspuwer", crate: "krat", pond: "plas slijm", stove: "heksenketel" },
  },
  premiseKey: "zombies",
  namePools: {
    A: { m: "Abaddon", f: "Agatha" }, B: { m: "Boris", f: "Bellatrix" }, C: { m: "Crispin", f: "Carmilla" },
    D: { m: "Dragomir", f: "Drusilla" }, E: { m: "Edgar", f: "Elvira" }, F: { m: "Fester", f: "Freya" },
    G: { m: "Grimm", f: "Griselda" }, H: { m: "Hades", f: "Hekate" }, I: { m: "Igor", f: "Isolde" },
    J: { m: "Jasper", f: "Jezebel" }, K: { m: "Krampus", f: "Karmilla" }, L: { m: "Lazarus", f: "Lilith" },
    M: { m: "Mortimer", f: "Morticia" }, N: { m: "Nero", f: "Nocturna" }, O: { m: "Osric", f: "Ophelia" },
    P: { m: "Pugsley", f: "Pandora" }, V: { m: "Vlad", f: "Vesper" },
  },
};

const ANIMALS: ThemeDefinition = {
  id: "animals",
  icons: animalsIcons,
  colours: {
    rooms: ["#e9e3ce", "#dbe7d2", "#e7ddcc", "#d7e3da", "#efe6d0", "#dee9d6", "#e5dfd0", "#d9e4ce", "#ece1cf", "#dce6dc"],
    suspectToken: "#4a3a22",
    victimToken: "#9c4a12",
    wall: "#3d3423",
    markMaybe: "#4a442f",
    markNo: "#a06033",
    roomLabel: "#6a5f45",
    plate: "#fbf8ef",
    icon: { stroke: "#8a7d5c", strokeBlocking: "#5c5136", fill: "#c2b08a", accent: "#93b6c4" },
  },
  objectNames: {
    en: { bed: "doghouse", chair: "wheelbarrow", carpet: "patch of grass", bench: "log", table: "anvil", cabinet: "barn", plant: "sunflower", tree: "oak", statue: "scarecrow", crate: "crate", pond: "duck pond", stove: "campfire" },
    nl: { bed: "hondenhok", chair: "kruiwagen", carpet: "plukje gras", bench: "boomstam", table: "aambeeld", cabinet: "schuur", plant: "zonnebloem", tree: "eik", statue: "vogelverschrikker", crate: "krat", pond: "eendenvijver", stove: "kampvuur" },
  },
  premiseKey: "animals",
  namePools: {
    A: { m: "Argos", f: "Abby" }, B: { m: "Bruno", f: "Bella" }, C: { m: "Cesar", f: "Cleo" },
    D: { m: "Duke", f: "Daisy" }, E: { m: "Elmo", f: "Elsa" }, F: { m: "Falco", f: "Fleur" },
    G: { m: "Goliath", f: "Gigi" }, H: { m: "Hachi", f: "Holly" }, I: { m: "Iko", f: "Iris" },
    J: { m: "Jaco", f: "Juno" }, K: { m: "Koda", f: "Kiki" }, L: { m: "Loki", f: "Luna" },
    M: { m: "Max", f: "Molly" }, N: { m: "Nero", f: "Nala" }, O: { m: "Odin", f: "Olga" },
    P: { m: "Pluto", f: "Poppy" }, V: { m: "Viggo", f: "Vera" },
  },
};

const ALIENS: ThemeDefinition = {
  id: "aliens",
  icons: aliensIcons,
  colours: {
    rooms: ["#d5dde4", "#d0e0dc", "#dad4e6", "#cfdbe8", "#d9dbd0", "#cde2e4", "#dcd6e0", "#d2e0d6", "#e0d8d8", "#d0d6e6"],
    suspectToken: "#1e2a3a",
    victimToken: "#7c1f4e",
    wall: "#1e2430",
    markMaybe: "#33404e",
    markNo: "#8e4a68",
    roomLabel: "#4e5a6a",
    plate: "#f4f7fa",
    icon: { stroke: "#6a7f8e", strokeBlocking: "#3a4652", fill: "#9fb2bc", accent: "#8fc48a" },
  },
  objectNames: {
    en: { bed: "cryo pod", chair: "pilot seat", carpet: "teleport pad", bench: "control desk", table: "vending machine", cabinet: "server rack", plant: "alien egg", tree: "radar dish", statue: "robot", crate: "cargo cube", pond: "acid pool", stove: "reactor" },
    nl: { bed: "cryocapsule", chair: "pilotenstoel", carpet: "teleportplaat", bench: "bedieningspaneel", table: "automaat", cabinet: "serverkast", plant: "buitenaards ei", tree: "radarschotel", statue: "robot", crate: "vrachtkubus", pond: "zuurpoel", stove: "reactor" },
  },
  premiseKey: "aliens",
  namePools: {
    A: { m: "Axxon", f: "Andra" }, B: { m: "Braxis", f: "Belara" }, C: { m: "Cygnus", f: "Cetra" },
    D: { m: "Drax", f: "Dione" }, E: { m: "Ezor", f: "Elara" }, F: { m: "Fobos", f: "Fyra" },
    G: { m: "Gorlak", f: "Galexa" }, H: { m: "Hypnos", f: "Hexa" }, I: { m: "Ixion", f: "Io" },
    J: { m: "Jarnok", f: "Jyx" }, K: { m: "Krell", f: "Kestra" }, L: { m: "Lorvax", f: "Lyra" },
    M: { m: "Mek", f: "Mira" }, N: { m: "Nibiru", f: "Nova" }, O: { m: "Orbulon", f: "Oona" },
    P: { m: "Proxor", f: "Pulsa" }, V: { m: "Vortan", f: "Vega" },
  },
};

const INSECTS: ThemeDefinition = {
  id: "insects",
  icons: insectsIcons,
  colours: {
    rooms: ["#e6e8cf", "#d8e6d0", "#e9e2ca", "#d4e4d8", "#eee8d2", "#dcead4", "#e2e0cc", "#d6e6ce", "#ece4ce", "#dae6da"],
    suspectToken: "#3a3d1e",
    victimToken: "#8c5a10",
    wall: "#33361f",
    markMaybe: "#44482c",
    markNo: "#9a7030",
    roomLabel: "#5f6242",
    plate: "#fafbef",
    icon: { stroke: "#8a8f5c", strokeBlocking: "#565a34", fill: "#bcbd88", accent: "#8fb6c8" },
  },
  objectNames: {
    en: { bed: "beehive", chair: "mushroom", carpet: "spiderweb", bench: "fallen log", table: "leaf", cabinet: "honeycomb", plant: "clover", tree: "pine tree", statue: "ant statue", crate: "acorn", pond: "dewdrop", stove: "candle" },
    nl: { bed: "bijenkorf", chair: "paddenstoel", carpet: "spinnenweb", bench: "boomstam", table: "blad", cabinet: "honingraat", plant: "klavertje", tree: "dennenboom", statue: "mierenbeeld", crate: "eikel", pond: "dauwdruppel", stove: "kaars" },
  },
  premiseKey: "insects",
  namePools: {
    A: { m: "Atlas", f: "Amber" }, B: { m: "Buzz", f: "Bibi" }, C: { m: "Chico", f: "Coco" },
    D: { m: "Dizzy", f: "Dot" }, E: { m: "Echo", f: "Evie" }, F: { m: "Flit", f: "Flora" },
    G: { m: "Grub", f: "Gala" }, H: { m: "Hummel", f: "Honey" }, I: { m: "Itchy", f: "Ivy" },
    J: { m: "Jitter", f: "June" }, K: { m: "Kafka", f: "Kira" }, L: { m: "Loek", f: "Luci" },
    M: { m: "Mantis", f: "Mona" }, N: { m: "Nipper", f: "Nina" }, O: { m: "Orius", f: "Opal" },
    P: { m: "Pincer", f: "Pearl" }, V: { m: "Vespa", f: "Vera" },
  },
};

export const THEMES: readonly ThemeDefinition[] = [CLASSIC, ZOMBIES, ANIMALS, ALIENS, INSECTS];

export const THEME_IDS = THEMES.map((theme) => theme.id);

/**
 * The theme for an id. Unknown ids fall back to classic rather than crash: a case whose theme was
 * ever removed from the registry must stay playable.
 */
export function themeFor(id: string | null | undefined): ThemeDefinition {
  return THEMES.find((theme) => theme.id === id) ?? CLASSIC;
}

/** The name a piece of furniture goes by in this theme and language. */
export function objectName(theme: ThemeDefinition, locale: string, kind: ObjectKind): string {
  const table = theme.objectNames[locale] ?? theme.objectNames.en;
  return table?.[kind] ?? kind;
}
